import { ArrowUp, ArrowDown, Minus } from 'lucide-react'

interface Props {
  before: number | null
  after: number | null
  className?: string
}

/**
 * Renders the numeric delta between two scores as a coloured chip.
 * - Improvement (after > before): green with up arrow
 * - Regression (after < before): red with down arrow
 * - No change: neutral with em-dash
 */
export default function ScoreDelta({ before, after, className }: Props) {
  if (before == null || after == null) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-white/5 border border-white/10 ${className ?? ''}`}
        style={{ color: 'var(--text-muted)' }}
      >
        <Minus size={11} />—
      </span>
    )
  }

  const delta = after - before

  if (delta === 0) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-white/5 border border-white/10 ${className ?? ''}`}
        style={{ color: 'var(--text-muted)' }}
      >
        <Minus size={11} />0
      </span>
    )
  }

  const positive = delta > 0
  const color = positive ? 'var(--score-good)' : 'var(--score-poor)'
  const bg = positive ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)'
  const border = positive ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full tabular-nums ${className ?? ''}`}
      style={{ color, background: bg, border: `1px solid ${border}` }}
    >
      {positive ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
      {positive ? `+${delta}` : delta}
    </span>
  )
}
